// 稽古を「声」が語る比較版の見た目 (DEV ?tutVoice=a|b|c|d・src/tut_voice.ts)。render.ts の drawTutorialFocus / drawTutorialPrompts から呼ぶ。
//   声は体を持たない (プロローグでは暗い画面の真ん中に金の明朝で出る・src/render.ts drawInterlude)。だから4案とも「誰かの札」ではなく「声が聞こえる」形にする。
//   a 金の囁き = 枠なし。声の金の明朝が、柔らかい暗がりの上に一字ずつ現れる (プロローグと同じ色と字)。照らしは茨のない細い金の輪。
//   b 吹き出し = 丸い半透明の吹き出し。尾は画面の外 (右上) へ点で消える (声の主は見えない)。ぽんと膨らんで出る。軽口の軽さ。
//   c 字幕     = 枠なしの字幕。左右に細い金の罫が伸び、下から浮いて出る。照らしは輪でなく柔らかい光だまり。
//   d レース   = 真鍮の鉄札の代わりに、すみれ色の薄布に真珠の縁取り。真ん中からほどけるように開く。茨の代わりに真珠。
//   色: 声の金 (drawInterlude と同じ 244,216,120)・象牙・すみれの墨。赤と青は使わない (青は常夜燈＝物語の芯に触れるので避ける)。
//   霜の輪 (tone 'frost'・体幹の溝) と暗幕は render.ts の今の描き方のまま。ここは札・ボタンの印・輪・線と、上の掲示だけ。
import { ARENA, PALETTE } from './balance'
import type { TutVoiceLook } from './tut_voice'
import { UI_SERIF } from './ui_font'

type Hole = { x: number; y: number; rx: number; ry: number; shape?: 'rect'; tone?: 'frost' }
type TextPos = 'top_left' | 'center' | 'above_boss_hud'

const FONT = UI_SERIF
const GOLD = (a: number) => `rgba(244,216,120,${a.toFixed(3)})`      // 声の金 (ADV の声と同じ)
const GOLD_HI = (a: number) => `rgba(255,240,196,${a.toFixed(3)})`
const IVORY = (a: number) => `rgba(248,238,220,${a.toFixed(3)})`
const PLUM = (a: number) => `rgba(30,18,40,${a.toFixed(3)})`         // すみれの墨
const PLUM_LT = (a: number) => `rgba(58,36,70,${a.toFixed(3)})`
const PEARL = (a: number) => `rgba(246,236,226,${a.toFixed(3)})`

const clamp01 = (v: number) => Math.max(0, Math.min(1, v))
const easeOut = (k: number) => 1 - (1 - k) * (1 - k)
/** 少し行き過ぎて戻る (吹き出しが膨らむ)。 */
const backOut = (k: number) => { const c = 1.9; const t = k - 1; return 1 + (c + 1) * t * t * t + c * t * t }

/** 一字ずつ出す。全部出し切るまでの ms は上限を切る (止めの最短表示 250〜600ms の内に読み終われる速さ)。 */
function typed(text: string, ms: number, fullMs: number): string {
  const chars = [...text]
  const n = Math.min(chars.length, Math.floor((ms / fullMs) * chars.length) + 1)
  return chars.slice(0, Math.max(0, n)).join('')
}

function measure(ctx: CanvasRenderingContext2D, font: string, text: string): number {
  ctx.save(); ctx.font = font; const w = ctx.measureText(text).width; ctx.restore()
  return Math.ceil(w)
}

/** 札の置き場所 (render.ts focusTextLayout と同じ考え方・字幕 c だけ高さを替える)。 */
function layout(look: TutVoiceLook, pos: TextPos, w: number): { x: number; y: number } {
  const cx = ARENA.width / 2
  if (look === 'c') {
    const y = pos === 'top_left' ? 150 : pos === 'above_boss_hud' ? 352 : 104
    return { x: cx - w / 2, y }
  }
  if (pos === 'top_left') return { x: 26, y: 112 }
  return { x: cx - w / 2, y: pos === 'above_boss_hud' ? 352 : 96 }
}

// ---------- 輪 ----------
function holePath(ctx: CanvasRenderingContext2D, h: Hole, sc: number): void {
  ctx.beginPath()
  if (h.shape === 'rect') ctx.roundRect(h.x - h.rx * sc, h.y - h.ry * sc, h.rx * sc * 2, h.ry * sc * 2, 8)
  else ctx.ellipse(h.x, h.y, h.rx * sc, h.ry * sc, 0, 0, Math.PI * 2)
}

function pointOn(h: Hole, sc: number, a: number): { x: number; y: number } {
  return { x: h.x + Math.cos(a) * h.rx * sc, y: h.y + Math.sin(a) * h.ry * sc }
}

function drawRing(ctx: CanvasRenderingContext2D, look: TutVoiceLook, h: Hole, alpha: number, pulse: number, ms: number): void {
  ctx.save()
  if (look === 'a') {
    // 細い金の輪。外へ柔らかく滲む。ゆっくり息をする。
    const sc = 0.9 + 0.02 * pulse
    ctx.shadowColor = GOLD(0.7 * alpha); ctx.shadowBlur = 10
    holePath(ctx, h, sc); ctx.strokeStyle = GOLD((0.55 + 0.25 * pulse) * alpha); ctx.lineWidth = 1.3; ctx.stroke()
    ctx.shadowBlur = 0
    holePath(ctx, h, sc * 1.035); ctx.strokeStyle = GOLD(0.18 * alpha); ctx.lineWidth = 0.8; ctx.stroke()
  } else if (look === 'b') {
    // 丸い象牙の輪と、周りを回る小さな点 (軽さ)。
    const sc = 0.9 + 0.025 * pulse
    holePath(ctx, h, sc); ctx.strokeStyle = PLUM(0.7 * alpha); ctx.lineWidth = 4; ctx.stroke()
    holePath(ctx, h, sc); ctx.strokeStyle = IVORY(0.85 * alpha); ctx.lineWidth = 2; ctx.stroke()
    const spin = (ms / 1000) * 0.6
    for (let i = 0; i < 3; i += 1) {
      const p = pointOn(h, sc, spin + (i * Math.PI * 2) / 3)
      ctx.fillStyle = IVORY(0.95 * alpha)
      ctx.beginPath(); ctx.arc(p.x, p.y, 3.2, 0, Math.PI * 2); ctx.fill()
    }
  } else if (look === 'c') {
    // 輪を描かない。縁に柔らかい光だまり (上が濃い)。
    ctx.globalCompositeOperation = 'lighter'
    const r = Math.max(h.rx, h.ry)
    const g = ctx.createRadialGradient(h.x, h.y - h.ry * 0.2, r * 0.55, h.x, h.y, r * 1.05)
    g.addColorStop(0, GOLD(0))
    g.addColorStop(0.8, GOLD((0.07 + 0.04 * pulse) * alpha))
    g.addColorStop(1, GOLD(0))
    ctx.fillStyle = g
    ctx.save(); ctx.translate(h.x, h.y); ctx.scale(h.rx / r, h.ry / r); ctx.translate(-h.x, -h.y)
    ctx.beginPath(); ctx.arc(h.x, h.y, r * 1.08, 0, Math.PI * 2); ctx.fill()
    ctx.restore()
  } else if (look === 'd') {
    // 細い象牙の輪に真珠を連ねる。上下左右は大きめの真珠。
    const sc = 0.9 + 0.015 * pulse
    holePath(ctx, h, sc); ctx.strokeStyle = PLUM(0.75 * alpha); ctx.lineWidth = 3; ctx.stroke()
    holePath(ctx, h, sc); ctx.strokeStyle = PEARL(0.7 * alpha); ctx.lineWidth = 1; ctx.stroke()
    const n = 20
    for (let i = 0; i < n; i += 1) {
      const a = (i / n) * Math.PI * 2
      const p = pointOn(h, sc, a)
      const big = i % 5 === 0
      pearl(ctx, p.x, p.y, big ? 3.6 : 1.7, alpha)
    }
  }
  ctx.restore()
}

function pearl(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, alpha: number): void {
  const g = ctx.createRadialGradient(x - r * 0.35, y - r * 0.4, r * 0.1, x, y, r)
  g.addColorStop(0, `rgba(255,255,255,${alpha.toFixed(3)})`)
  g.addColorStop(0.6, PEARL(0.95 * alpha))
  g.addColorStop(1, `rgba(196,176,190,${(0.9 * alpha).toFixed(3)})`)
  ctx.fillStyle = g
  ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill()
}

// ---------- 札 (声の一言) ----------
/** 声の一言の地と字。(x,y) は左上・w,h は札の大きさ。k = 出てきた度合い (0..1)。 */
function drawSpeech(ctx: CanvasRenderingContext2D, look: TutVoiceLook, x: number, y: number, w: number, h: number,
  text: string, fontPx: number, alpha: number, k: number, tag: boolean): void {
  const cx = x + w / 2, cy = y + h / 2
  ctx.save()
  if (look === 'a') {
    // 柔らかい暗がり (縁のない楕円) の上に、金の字。
    const g = ctx.createRadialGradient(cx, cy, 4, cx, cy, w * 0.62)
    g.addColorStop(0, `rgba(8,5,12,${(0.72 * alpha).toFixed(3)})`)
    g.addColorStop(0.7, `rgba(8,5,12,${(0.42 * alpha).toFixed(3)})`)
    g.addColorStop(1, 'rgba(8,5,12,0)')
    ctx.save(); ctx.translate(cx, cy); ctx.scale(1, (h * 1.5) / w); ctx.translate(-cx, -cy)
    ctx.fillStyle = g; ctx.beginPath(); ctx.arc(cx, cy, w * 0.62, 0, Math.PI * 2); ctx.fill()
    ctx.restore()
    ctx.font = `500 ${fontPx}px ${FONT}`
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
    ctx.shadowColor = GOLD(0.45 * alpha); ctx.shadowBlur = 12
    ctx.fillStyle = GOLD(0.95 * alpha)
    if (text) ctx.fillText(text, cx, cy + 1)
  } else if (look === 'b') {
    const s = 0.6 + 0.4 * backOut(k)
    ctx.translate(cx, cy); ctx.scale(s, s); ctx.translate(-cx, -cy)
    // 尾: 右上の画面の外へ、小さくなる点。
    const tx = x + w - 30, ty = y
    for (let i = 0; i < 3; i += 1) {
      const r = 6 - i * 1.7
      ctx.fillStyle = PLUM(0.88 * alpha)
      ctx.strokeStyle = GOLD_HI(0.75 * alpha); ctx.lineWidth = 1.2
      ctx.beginPath(); ctx.arc(tx + 12 + i * 14, ty - 8 - i * 11, r, 0, Math.PI * 2); ctx.fill(); ctx.stroke()
    }
    ctx.beginPath(); ctx.roundRect(x, y, w, h, h / 2)
    ctx.fillStyle = PLUM(0.88 * alpha); ctx.fill()
    ctx.strokeStyle = GOLD_HI(0.75 * alpha); ctx.lineWidth = 1.5; ctx.stroke()
    ctx.font = `500 ${fontPx}px ${FONT}`
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
    ctx.fillStyle = IVORY(alpha)
    ctx.fillText(text, cx, cy + 1)
    if (tag) nameTag(ctx, x + 18, y, alpha)
  } else if (look === 'c') {
    const lift = (1 - k) * 8
    ctx.translate(0, lift)
    ctx.font = `500 ${fontPx}px ${FONT}`
    const tw = measure(ctx, ctx.font, text)
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
    // 左右の細い金の罫 (外へ消える)。
    const ruleW = 70 + 50 * k
    for (const sgn of [-1, 1]) {
      const x0 = cx + sgn * (tw / 2 + 16), x1 = x0 + sgn * ruleW
      const g = ctx.createLinearGradient(x0, 0, x1, 0)
      g.addColorStop(0, GOLD(0.75 * alpha)); g.addColorStop(1, GOLD(0))
      ctx.strokeStyle = g; ctx.lineWidth = 1
      ctx.beginPath(); ctx.moveTo(x0, cy + 1); ctx.lineTo(x1, cy + 1); ctx.stroke()
    }
    ctx.lineJoin = 'round'
    ctx.strokeStyle = `rgba(6,4,10,${(0.85 * alpha).toFixed(3)})`; ctx.lineWidth = 5
    ctx.strokeText(text, cx, cy + 1)
    ctx.fillStyle = GOLD(alpha)
    ctx.fillText(text, cx, cy + 1)
  } else if (look === 'd') {
    // 真ん中から左右へほどける。
    const open = easeOut(k)
    ctx.beginPath(); ctx.rect(cx - (w / 2 + 12) * open, y - 16, (w + 24) * open, h + 32); ctx.clip()
    const g = ctx.createLinearGradient(0, y, 0, y + h)
    g.addColorStop(0, PLUM_LT(0.9 * alpha)); g.addColorStop(1, PLUM(0.92 * alpha))
    ctx.beginPath(); ctx.roundRect(x, y, w, h, 14)
    ctx.fillStyle = g; ctx.fill()
    ctx.strokeStyle = PEARL(0.55 * alpha); ctx.lineWidth = 1
    ctx.beginPath(); ctx.roundRect(x + 5, y + 5, w - 10, h - 10, 10); ctx.stroke()
    // 縁の真珠 (上下の縁に並べる・角は大きめ)。
    const step = 11
    for (let px = x + 16; px <= x + w - 16; px += step) {
      pearl(ctx, px, y, 1.8, alpha)
      pearl(ctx, px, y + h, 1.8, alpha)
    }
    for (const [qx, qy] of [[x + 6, y + 6], [x + w - 6, y + 6], [x + 6, y + h - 6], [x + w - 6, y + h - 6]] as const) pearl(ctx, qx, qy, 3.2, alpha)
    ctx.font = `500 ${fontPx}px ${FONT}`
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
    ctx.globalAlpha = clamp01((open - 0.5) * 2)
    ctx.fillStyle = IVORY(alpha)
    ctx.fillText(text, cx, cy + 1)
    if (tag) { ctx.restore(); ctx.save(); ctx.globalAlpha = clamp01((open - 0.5) * 2); medallion(ctx, cx, y, alpha) }
  }
  ctx.restore()
}

/** 吹き出しの左上の「声」の名札。 */
function nameTag(ctx: CanvasRenderingContext2D, x: number, y: number, alpha: number): void {
  ctx.save()
  ctx.font = `600 13px ${FONT}`
  ctx.beginPath(); ctx.roundRect(x, y - 10, 34, 20, 10)
  ctx.fillStyle = GOLD(0.95 * alpha); ctx.fill()
  ctx.fillStyle = PLUM(alpha); ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
  ctx.fillText('声', x + 17, y + 1)
  ctx.restore()
}

/** レースの札の上の縁の真ん中の「声」の小さな円章。 */
function medallion(ctx: CanvasRenderingContext2D, x: number, y: number, alpha: number): void {
  ctx.beginPath(); ctx.arc(x, y, 11, 0, Math.PI * 2)
  ctx.fillStyle = PLUM(0.95 * alpha); ctx.fill()
  ctx.strokeStyle = PEARL(0.8 * alpha); ctx.lineWidth = 1; ctx.stroke()
  for (let i = 0; i < 8; i += 1) { const a = (i / 8) * Math.PI * 2; pearl(ctx, x + Math.cos(a) * 11, y + Math.sin(a) * 11, 1.4, alpha) }
  ctx.font = `600 12px ${FONT}`
  ctx.fillStyle = IVORY(alpha); ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
  ctx.fillText('声', x, y + 1)
}

// ---------- ボタンの印 ----------
function drawKey(ctx: CanvasRenderingContext2D, look: TutVoiceLook, x: number, y: number, w: number, h: number, label: string, alpha: number, scale: number): void {
  ctx.save()
  const cx = x + w / 2, cy = y + h / 2
  ctx.translate(cx, cy); ctx.scale(scale, scale); ctx.translate(-cx, -cy)
  ctx.font = `600 ${Math.round(h * 0.58)}px ${FONT}`
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
  if (look === 'a') {
    ctx.beginPath(); ctx.roundRect(x, y, w, h, h / 2)
    ctx.fillStyle = `rgba(8,5,12,${(0.55 * alpha).toFixed(3)})`; ctx.fill()
    ctx.strokeStyle = GOLD(0.85 * alpha); ctx.lineWidth = 1; ctx.stroke()
    ctx.fillStyle = GOLD(alpha); ctx.fillText(label, cx, cy + 1)
  } else if (look === 'b') {
    ctx.beginPath(); ctx.roundRect(x, y, w, h, h / 2)
    ctx.fillStyle = IVORY(0.95 * alpha); ctx.fill()
    ctx.fillStyle = PLUM(alpha); ctx.fillText(label, cx, cy + 1)
  } else if (look === 'c') {
    ctx.strokeStyle = `rgba(6,4,10,${(0.8 * alpha).toFixed(3)})`; ctx.lineWidth = 4
    ctx.strokeText(label, cx, cy)
    ctx.fillStyle = GOLD_HI(alpha); ctx.fillText(label, cx, cy)
    ctx.strokeStyle = GOLD(0.8 * alpha); ctx.lineWidth = 1
    ctx.beginPath(); ctx.moveTo(x + 6, y + h - 1); ctx.lineTo(x + w - 6, y + h - 1); ctx.stroke()
  } else if (look === 'd') {
    ctx.beginPath(); ctx.roundRect(x, y, w, h, 9)
    ctx.fillStyle = PLUM(0.9 * alpha); ctx.fill()
    ctx.strokeStyle = PEARL(0.75 * alpha); ctx.lineWidth = 1; ctx.stroke()
    pearl(ctx, x, cy, 3, alpha); pearl(ctx, x + w, cy, 3, alpha)
    ctx.fillStyle = IVORY(alpha); ctx.fillText(label, cx, cy + 1)
  }
  ctx.restore()
}

function drawHint(ctx: CanvasRenderingContext2D, look: TutVoiceLook, x: number, y: number, text: string, alpha: number): void {
  ctx.save()
  ctx.font = `500 16px ${FONT}`
  ctx.textAlign = 'left'; ctx.textBaseline = 'middle'
  ctx.strokeStyle = `rgba(6,4,10,${(0.8 * alpha).toFixed(3)})`; ctx.lineWidth = 4; ctx.lineJoin = 'round'
  ctx.strokeText(text, x, y)
  ctx.fillStyle = look === 'a' || look === 'c' ? GOLD(alpha) : IVORY(alpha)
  ctx.fillText(text, x, y)
  ctx.restore()
}

// ---------- 線 ----------
function drawLeader(ctx: CanvasRenderingContext2D, look: TutVoiceLook, sx: number, sy: number, ex: number, ey: number, alpha: number, ms: number): void {
  if (look === 'c') return   // 字幕は線を引かない (光だまりだけで見る所を示す)
  const mx = (sx + ex) / 2, my = Math.min(sy, ey) + Math.abs(ey - sy) * 0.15
  const bez = (t: number) => ({
    x: (1 - t) * (1 - t) * sx + 2 * (1 - t) * t * mx + t * t * ex,
    y: (1 - t) * (1 - t) * sy + 2 * (1 - t) * t * my + t * t * ey,
  })
  ctx.save()
  if (look === 'b') {
    // 小さな丸の点線が、印から照らしへ流れる。
    const flow = (ms / 1000) * 0.8
    for (let i = 0; i < 18; i += 1) {
      const t = ((i / 18) + flow) % 1
      const p = bez(t)
      ctx.fillStyle = IVORY(0.85 * alpha)
      ctx.beginPath(); ctx.arc(p.x, p.y, 1.8, 0, Math.PI * 2); ctx.fill()
    }
  } else {
    ctx.beginPath(); ctx.moveTo(sx, sy); ctx.quadraticCurveTo(mx, my, ex, ey)
    ctx.strokeStyle = look === 'a' ? GOLD(0.6 * alpha) : PEARL(0.6 * alpha)
    ctx.lineWidth = 1
    if (look === 'a') { ctx.shadowColor = GOLD(0.6 * alpha); ctx.shadowBlur = 6 }
    ctx.stroke()
    ctx.shadowBlur = 0
    const p = bez(0.5)
    if (look === 'd') pearl(ctx, p.x, p.y, 2.8, alpha)
    else { ctx.fillStyle = GOLD(0.9 * alpha); ctx.beginPath(); ctx.arc(ex, ey, 2.2, 0, Math.PI * 2); ctx.fill() }
  }
  ctx.restore()
}

// ---------- 止めの場面 ----------
export type VoiceFocusArgs = {
  look: Exclude<TutVoiceLook, 'text'>
  line: string; badge: string; hint: string
  ms: number; ease: number; ready: number; pulse: number
  holes: Hole[]; textPos: TextPos; leaderTo?: number
  edgeToward: (h: Hole, sx: number, sy: number) => { x: number; y: number }
}

/** 止めた場面の、輪 (霜の輪を除く)・声の一言・ボタンの印・線。暗幕は描き終えてから呼ぶ。 */
export function drawVoiceFocus(ctx: CanvasRenderingContext2D, a: VoiceFocusArgs): void {
  const { look } = a
  for (const h of a.holes) if (h.tone !== 'frost') drawRing(ctx, look, h, a.ease, a.pulse, a.ms)
  const fontPx = look === 'c' ? 22 : 21
  const font = `500 ${fontPx}px ${FONT}`
  const fullW = measure(ctx, font, a.line)
  const padX = look === 'b' ? 34 : look === 'd' ? 40 : 48
  const w = fullW + padX * 2, h = look === 'b' ? 46 : 44
  const k = clamp01(a.ms / (look === 'b' ? 240 : 300))
  const shown = look === 'a' ? typed(a.line, a.ms, 320) : a.line
  // 一字ずつの時は、字の頭を揃える (出し切った姿と同じ所から書く)。
  const L = layout(look, a.textPos, w)
  if (look === 'a' && shown !== a.line) {
    drawSpeech(ctx, look, L.x, L.y, w, h, '', fontPx, a.ease, k, true)
    ctx.save(); ctx.font = font
    ctx.textAlign = 'left'; ctx.textBaseline = 'middle'
    ctx.shadowColor = GOLD(0.45 * a.ease); ctx.shadowBlur = 12; ctx.fillStyle = GOLD(0.95 * a.ease)
    ctx.fillText(shown, L.x + padX, L.y + h / 2 + 1)
    ctx.restore()
  } else {
    drawSpeech(ctx, look, L.x, L.y, w, h, shown, fontPx, a.ease * (look === 'c' ? easeOut(k) : 1), k, true)
  }

  const keyH = 28
  const keyW = measure(ctx, `600 ${Math.round(keyH * 0.58)}px ${FONT}`, a.badge) + 28
  const hintW = a.hint ? measure(ctx, `500 16px ${FONT}`, a.hint) + 12 : 0
  const rowW = keyW + hintW
  const bx = a.textPos === 'top_left' && look !== 'c' ? L.x + 8 : ARENA.width / 2 - rowW / 2
  const by = L.y + h + (look === 'b' || look === 'd' ? 16 : 10)
  const keyAlpha = a.ease * (0.45 + 0.55 * a.ready) * (look === 'a' ? clamp01((a.ms - 120) / 200) : 1)
  drawKey(ctx, look, bx, by, keyW, keyH, a.badge, keyAlpha, 1 + (a.ready >= 1 ? 0.05 * a.pulse : 0))
  if (a.hint) drawHint(ctx, look, bx + keyW + 10, by + keyH / 2 + 1, a.hint, keyAlpha)
  const target = a.leaderTo !== undefined ? a.holes[a.leaderTo] : undefined
  if (target) {
    const sx = bx + keyW / 2, sy = by + keyH + 3
    const e = a.edgeToward(target, sx, sy)
    drawLeader(ctx, look, sx, sy, e.x, e.y, a.ease * 0.9, a.ms)
  }
}

// ---------- 上の掲示 ----------
export type VoiceBannerArgs = {
  look: Exclude<TutVoiceLook, 'text'>
  header: string; text: string
  total: number; filled: number; showCount: boolean
  /** この文を出し始めてからの秒 (出方の演出用)。 */
  age: number
  /** ボスの攻撃中 (掲示を少し脈打たせる・今の掲示と同じ合図)。 */
  inAttack: boolean
  time: number
}

/** 稽古の上の掲示 (見出し・声の一文・成功の印)。 */
export function drawVoiceBanner(ctx: CanvasRenderingContext2D, a: VoiceBannerArgs): void {
  const { look } = a
  const cx = ARENA.width / 2
  const blink = a.inAttack ? 0.82 + 0.18 * Math.sin(a.time * 8) : 1
  const ms = a.age * 1000
  const fontPx = 19
  const font = `500 ${fontPx}px ${FONT}`
  const tw = measure(ctx, font, a.text)
  const padX = look === 'b' ? 30 : 44
  const w = tw + padX * 2
  const h = look === 'b' || look === 'd' ? 78 : 40
  const y = look === 'b' || look === 'd' ? 88 : 104
  const x = cx - w / 2
  const k = clamp01(ms / (look === 'b' ? 240 : 320))
  ctx.save()
  if (look === 'b' || look === 'd') {
    // 吹き出し・レースの札の中に、見出し・声・印を収める。
    drawSpeech(ctx, look, x, y, w, h, '', fontPx, blink, k, look === 'b')
    const open = look === 'd' ? clamp01((easeOut(k) - 0.5) * 2) : 1
    const s = look === 'b' ? 0.6 + 0.4 * backOut(k) : 1
    ctx.save()
    ctx.translate(cx, y + h / 2); ctx.scale(s, s); ctx.translate(-cx, -(y + h / 2))
    ctx.globalAlpha = open
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
    ctx.font = `500 12px ${FONT}`
    ctx.fillStyle = look === 'b' ? GOLD(0.85) : PEARL(0.7)
    ctx.fillText(a.header, cx, y + 20)
    ctx.font = font
    ctx.fillStyle = IVORY(blink)
    ctx.fillText(a.text, cx, y + 43)
    if (a.showCount) dots(ctx, look, cx, y + 64, a.total, a.filled, 1)
    ctx.restore()
    if (look === 'd') { ctx.globalAlpha = open; medallion(ctx, cx, y, 1) }
  } else if (look === 'a') {
    const shown = typed(a.text, ms, Math.min(900, 30 * [...a.text].length))
    // 見出しは小さく静かに。声は暗がりの上の金。
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
    ctx.font = `500 13px ${FONT}`
    ctx.fillStyle = GOLD(0.55)
    ctx.fillText(a.header, cx, 86)
    drawSpeech(ctx, look, x, y, w, h, '', fontPx, 1, k, false)
    ctx.font = font; ctx.textAlign = 'left'
    ctx.shadowColor = GOLD(0.4); ctx.shadowBlur = 10
    ctx.fillStyle = GOLD(0.95 * blink)
    ctx.fillText(shown, cx - tw / 2, y + h / 2 + 1)
    ctx.shadowBlur = 0
    if (a.showCount) dots(ctx, look, cx, y + h + 10, a.total, a.filled, 1)
  } else {
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
    ctx.font = `500 13px ${FONT}`
    ctx.strokeStyle = 'rgba(6,4,10,0.8)'; ctx.lineWidth = 3; ctx.lineJoin = 'round'
    ctx.strokeText(a.header, cx, 84)
    ctx.fillStyle = GOLD(0.6)
    ctx.fillText(a.header, cx, 84)
    drawSpeech(ctx, look, x, y, w, h, a.text, fontPx, easeOut(k) * blink, k, false)
    if (a.showCount) dots(ctx, look, cx, y + h + 10, a.total, a.filled, 1)
  }
  ctx.restore()
}

/** 成功の印。a 金の点／b 丸／c 菱形／d 真珠。 */
function dots(ctx: CanvasRenderingContext2D, look: TutVoiceLook, cx: number, y: number, total: number, filled: number, alpha: number): void {
  const gap = look === 'c' ? 18 : 20
  const x0 = cx - ((total - 1) * gap) / 2
  for (let i = 0; i < total; i += 1) {
    const x = x0 + i * gap
    const on = i < filled
    ctx.save()
    if (look === 'd') {
      if (on) pearl(ctx, x, y, 4.6, alpha)
      else { ctx.strokeStyle = PEARL(0.5 * alpha); ctx.lineWidth = 1; ctx.beginPath(); ctx.arc(x, y, 4, 0, Math.PI * 2); ctx.stroke() }
    } else if (look === 'c') {
      ctx.beginPath(); ctx.moveTo(x, y - 5); ctx.lineTo(x + 4, y); ctx.lineTo(x, y + 5); ctx.lineTo(x - 4, y); ctx.closePath()
      if (on) { ctx.fillStyle = GOLD(alpha); ctx.fill() } else { ctx.strokeStyle = GOLD(0.5 * alpha); ctx.lineWidth = 1; ctx.stroke() }
    } else {
      ctx.beginPath(); ctx.arc(x, y, look === 'b' ? 4.5 : 3.6, 0, Math.PI * 2)
      if (on) {
        if (look === 'a') { ctx.shadowColor = GOLD(0.8 * alpha); ctx.shadowBlur = 8 }
        ctx.fillStyle = look === 'b' ? PALETTE.fxGold : GOLD(alpha); ctx.fill()
      } else { ctx.strokeStyle = look === 'b' ? IVORY(0.55 * alpha) : GOLD(0.45 * alpha); ctx.lineWidth = 1.2; ctx.stroke() }
    }
    ctx.restore()
  }
}

/** 戦闘中の声のひと言 (DEV ?hud=v2・src/hud_v2.ts)。稽古の a (金の囁き) と同じ見え方で、画面の一番上の真ん中に一字ずつ出す。
 *  稽古の y=104 だと立ち上がったボスの頭 (致命の紫の予兆) に重なる (10-31 灰の戴冠獣で撮って確かめた) ので上へ寄せる。
 *  字は1字 30ms で出す。instant = 一度に出す (崩れた時。覚醒獣は崩れてから約0.3秒で紫が来る)。
 *  図形 (線・輪・縁) は足さない。age = 出始めてからの秒数・sec = 出しておく秒数。 */
export function drawBattleWhisper(ctx: CanvasRenderingContext2D, text: string, age: number, sec: number, instant = false): void {
  const fontPx = 19
  const font = `500 ${fontPx}px ${FONT}`
  const w = Math.max(260, measure(ctx, font, text) + 64), h = 40
  const x = 480 - w / 2, y = 22
  const alpha = clamp01(age / (instant ? 0.08 : 0.25)) * clamp01((sec - age) / 0.5)
  const ms = age * 1000
  drawSpeech(ctx, 'a', x, y, w, h, instant ? text : typed(text, ms, [...text].length * 30), fontPx, alpha, easeOut(clamp01(age / 0.3)), false)
}
